import type { SerialNumber } from "~domain/devices/models/common-device-model.ts";
import type { ModulatorLinkCsId } from "~domain/devices/models/links/link-model.ts";
import type { ModulatorCsId, ModulatorHwId } from "~domain/devices/models/modulators/modulator-model.ts";
import type { PilotHwId } from "~domain/devices/models/pilot/pilot-model.ts";
import type { SiteId } from "~domain/sites/models/site-model.ts";

import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { backofficeAcqRateResponseSchema } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-acq-rate-response.ts";
import { backofficeFindAppliancesResponseSchema } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-appliances-response.ts";
import { backofficeFindGprsConsoResponseSchema } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-gprs-conso-response.ts";
import { backofficeFindLinksResponseSchema } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-links-response.ts";
import { backofficeFindModulatorLastTemperaturePointResponseSchema } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-modulator-last-temperature-point-response.ts";
import { backofficeFindModulatorResponseSchema } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-modulator-response.ts";
import { backofficeFindPilotComponentsResponseSchema } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-pilot-components-response.ts";
import { backofficeFindPilotDataInfosResponseSchema } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-pilot-data-infos-response.ts";
import { backofficeFindPilotLastConnectionResponseSchema } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-pilot-last-connection-response.ts";
import { backofficeFindPilotModulatorsResponseSchema } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-pilot-modulators-response.ts";
import { backofficeFindPilotResponseSchema } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-pilot-response.ts";
import { backofficeFindThirdPartyAppliancesResponseSchema } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-third-party-appliances-response.ts";
import { backofficeLastPointResponseSchema } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-last-point-response.ts";
import { backofficeModulatorConsumptionResponseSchema } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-modulator-consumption-response.ts";

const c = initContract();

export const devicesBackofficeContract = c.router(
  {
    findModulatorDetails: {
      method: "GET",
      path: "/api/modulator/:modulatorHwId",
      pathParams: z.object({
        modulatorHwId: z.number(),
      }),
      responses: {
        200: backofficeFindModulatorResponseSchema,
      },
    },
    findModulatorAcqRateLastDay: {
      method: "GET",
      path: "/api/modulator/:modulatorHwId/acq-rate-24h",
      pathParams: z.object({
        modulatorHwId: z.number().transform((s) => s as ModulatorHwId),
      }),
      responses: {
        200: backofficeAcqRateResponseSchema,
      },
    },
    findModulatorAcqRateLastWeek: {
      method: "GET",
      path: "/api/modulator/:modulatorHwId/acq-rate-7d",
      pathParams: z.object({
        modulatorHwId: z.number().transform((s) => s as ModulatorHwId),
      }),
      responses: {
        200: backofficeAcqRateResponseSchema,
      },
    },
    findModulatorAcqRateLastMonth: {
      method: "GET",
      path: "/api/modulator/:modulatorHwId/acq-rate-30d",
      pathParams: z.object({
        modulatorHwId: z.number().transform((s) => s as ModulatorHwId),
      }),
      responses: {
        200: backofficeAcqRateResponseSchema,
      },
    },
    findModulatorLastPoint: {
      method: "GET",
      path: "/api/modulator/:modulatorCsId/last-point",
      pathParams: z.object({
        modulatorCsId: z.number().transform((s) => s as ModulatorCsId),
      }),
      responses: {
        200: backofficeLastPointResponseSchema,
      },
    },
    findModulatorConsumptions: {
      method: "GET",
      path: "/api/modulator/:modulatorCsId/consumption",
      pathParams: z.object({
        modulatorCsId: z.number().transform((s) => s as ModulatorCsId),
      }),
      query: z.object({
        start: z.string(),
        end: z.string(),
      }),
      responses: {
        200: backofficeModulatorConsumptionResponseSchema.array(),
      },
    },
    findModulatorLinks: {
      method: "GET",
      path: "/api/modulator/:modulatorHwId/links",
      pathParams: z.object({
        modulatorHwId: z.number().transform((s) => s as ModulatorHwId),
      }),
      responses: {
        200: backofficeFindLinksResponseSchema,
      },
    },
    findLinkAppliances: {
      method: "GET",
      path: "/api/link/:linkCsId/appliances",
      pathParams: z.object({
        linkCsId: z.number().transform((s) => s as ModulatorLinkCsId),
      }),
      responses: {
        200: backofficeFindAppliancesResponseSchema,
      },
    },
    findPilot: {
      method: "GET",
      path: "/api/mombox/:pilotHwId",
      pathParams: z.object({
        pilotHwId: z.number().transform((s) => s as PilotHwId),
      }),
      responses: {
        200: backofficeFindPilotResponseSchema,
      },
    },
    findPilotGprsConso: {
      method: "GET",
      path: "/api/mombox/:pilotHwId/gprs-conso",
      pathParams: z.object({
        pilotHwId: z.number().transform((s) => s as PilotHwId),
      }),
      responses: {
        200: backofficeFindGprsConsoResponseSchema,
      },
    },
    findPilotLastConnection: {
      method: "GET",
      path: "/api/mombox/:pilotHwId/last-connection",
      pathParams: z.object({
        pilotHwId: z.number().transform((s) => s as PilotHwId),
      }),
      responses: {
        200: backofficeFindPilotLastConnectionResponseSchema,
      },
    },
    findPilotComponents: {
      method: "GET",
      path: "/api/mombox/:pilotHwId/components",
      pathParams: z.object({
        pilotHwId: z.number().transform((s) => s as PilotHwId),
      }),
      responses: {
        200: backofficeFindPilotComponentsResponseSchema,
      },
    },
    findPilotDataFrames: {
      method: "GET",
      path: "/api/mombox/:pilotHwId/dataInfos",
      pathParams: z.object({
        pilotHwId: z.number().transform((s) => s as PilotHwId),
      }),
      responses: {
        200: backofficeFindPilotDataInfosResponseSchema,
      },
    },
    findPilotModulators: {
      method: "GET",
      path: "/api/mombox/:pilotHwId/modulators",
      pathParams: z.object({
        pilotHwId: z.number().transform((s) => s as PilotHwId),
      }),
      responses: {
        200: backofficeFindPilotModulatorsResponseSchema,
      },
    },
    findThirdPartySensorAppliances: {
      method: "GET",
      path: "/api/third-party/cs/:thirdPartySensorCsId",
      pathParams: z.object({
        thirdPartySensorCsId: z.number().transform((s) => s as ModulatorCsId),
      }),
      responses: {
        200: backofficeFindThirdPartyAppliancesResponseSchema,
        404: z.void(), // 404 returns nothing
      },
    },
    deleteThirdPartyAssociation: {
      method: "DELETE",
      path: "/api/third-party/sensor/:thirdPartySensorCsId/appliance/:applianceCsId",
      pathParams: z.object({
        thirdPartySensorCsId: z.number().transform((s) => s as ModulatorCsId),
        applianceCsId: z.number().transform((s) => s as ModulatorCsId),
      }),
      responses: {
        200: z.void(), // return nothing
      },
    },
    updatePilotVersion: {
      method: "PUT",
      path: "/api/vx/device/site/:siteId/gateway/:serialNumber/update",
      pathParams: z.object({
        serialNumber: z.string().transform((s) => s as SerialNumber),
        siteId: z.number().transform((s) => s as SiteId),
      }),
      body: z.void(),
      responses: {
        200: z.unknown(),
      },
    },
    findModulatorsLastTemperaturePoint: {
      method: "GET",
      path: "/api/modulator/last-temperature-point",
      query: z.object({
        csModulators: z
          .number()
          .transform((s) => s as ModulatorCsId)
          .array(),
      }),
      responses: {
        200: backofficeFindModulatorLastTemperaturePointResponseSchema,
      },
    },
  },
  {
    strictStatusCodes: true,
  },
);
